import { useState, useCallback, useRef, useEffect } from 'react'
import { useSelector } from 'react-redux'

import RegenerateIcon from 'assets/svgr-icons/Refresh.svg'
import PaletteIcon from 'assets/svgr-icons/Palette.svg'
import AnimateIcon from 'assets/svgr-icons/Animate.svg'
import DownloadIcon from 'assets/svgr-icons/Download.svg'
import VersionHistoryIcon from 'assets/svgr-icons/versionHistory.svg'
import { ButtonWithIcon, Text } from 'common-components'
import { T } from 'consts/translationKeys'
import TagList from 'components/TagList'
import AIPromptText from 'components/AIPromptText'
import { aiImageThunks } from 'store/features/aiImage/thunks'
import { aiImageSelectors } from 'store/features/aiImage/selectors'
import { translate } from 'utils/translations'
import * as Styled from './styled'
import useTags from './useTags'
import useActions from './useActions'
import { dispatch } from 'store'
import { normalizeAspectRatio } from 'utils/aspectRatio'
import ImageContainer from 'components/ImageContainer'
import { isBlobUrl } from 'utils'

const AIImageOverlayContent = () => {
  const aiImageOverlayData = useSelector(aiImageSelectors.aiImageOverlayData)
  const shouldClearGenerationParams = useRef<boolean>(true)
  const aspectRatio = useSelector(aiImageSelectors.aspectRatio)
  const aspectRatioValue = normalizeAspectRatio(aspectRatio)
  const formData = useSelector(aiImageSelectors.generateForm)
  const { images } = formData
  const aiImagePath = images[0]?.filePath || ''

  const isBlobImage = isBlobUrl(aiImagePath)

  const tags = useTags()

  const {
    handleRegenerate,
    handleUseAsReference,
    handleAnimate,
    handleDownload,
    handleVersionHistory
  } = useActions()

  const handleVersionHistoryClick = useCallback(() => {
    shouldClearGenerationParams.current = false
    handleVersionHistory()
  }, [handleVersionHistory])

  const [isImageLoading, setIsImageLoading] = useState(true)

  const handleUseAsReferenceClick = useCallback(() => {
    shouldClearGenerationParams.current = false
    handleUseAsReference()
  }, [handleUseAsReference])

  const handleRegenerateClick = useCallback(() => {
    shouldClearGenerationParams.current = false
    handleRegenerate()
  }, [handleRegenerate])

  useEffect(() => {
    return () => {
      if (shouldClearGenerationParams.current) {
        dispatch(aiImageThunks.resetGenerateForm())
      }
    }
  }, [])

  return (
    <Styled.Container>
      <Styled.Header>
        <Text type="h1" color="secondaryDark" fontWeight="600">
          {translate(T.EDIT_AI_IMAGE)}
        </Text>
      </Styled.Header>
      <Styled.Content>
        <TagList tags={tags} />
        <Styled.ImageWrapper>
          <ImageContainer
            filePath={aiImageOverlayData.aiImagePath}
            isImageLoading={isImageLoading}
            onImageLoad={() => setIsImageLoading(false)}
            aspectRatio={aspectRatioValue}
          />
        </Styled.ImageWrapper>
        {aiImageOverlayData.prompt && (
          <AIPromptText promptText={aiImageOverlayData.prompt} />
        )}
        <Styled.ActionsList>
          <ButtonWithIcon
            icon={<AnimateIcon />}
            label={translate(T.ANIMATE)}
            onClick={handleAnimate}
            isDisabled={isBlobImage}
          />
          <ButtonWithIcon
            icon={<RegenerateIcon />}
            label={translate(T.RE_GENERATE)}
            onClick={handleRegenerateClick}
          />
          <ButtonWithIcon
            icon={<PaletteIcon />}
            label={translate(T.USE_AS_REFERENCE)}
            onClick={handleUseAsReferenceClick}
          />
          <ButtonWithIcon
            icon={<VersionHistoryIcon />}
            label={translate(T.VERSION_HISTORY_LABEL)}
            onClick={handleVersionHistoryClick}
          />
          <ButtonWithIcon
            icon={<DownloadIcon />}
            label={translate(T.DOWNLOAD)}
            onClick={handleDownload}
          />
        </Styled.ActionsList>
      </Styled.Content>
    </Styled.Container>
  )
}

export default AIImageOverlayContent
